import {pushError} from '@js/actions/errorManagement';

import I18n from '@js/modules/translations';

import {FetchTimeout} from '@js/components/modules/constants';

import Notification from '@js/components/layouts/notification';

import MapInterface from '@js/modules/maps/mapInterface';
import Polyline from '@js/modules/maps/polyline';
import MapRequest from '@js/modules/maps/mapRequest';

import AnalyticsService from '@js/modules/analyticsService';
import StorageService from '@js/modules/storage';


const ComputeRoadMapModule = {
    // _computeRoadModel: {},

    getSnapToRoadsRoute(startPointLatLng, endPointLatLng, wayPointLatLng, callback, {
        limitDistance = false,
        distance = 0,
        routeType = undefined
    } = {}) {
        if (!startPointLatLng || !endPointLatLng) {
            return;
        }

        if (limitDistance) {
            const distanceBetweenPoints = this.distHaversine(startPointLatLng, endPointLatLng, MapInterface.COORDINATES_PRECISION);
            const totalDistance = distance * 1000 + distanceBetweenPoints;
            const isBike = this._model.routingRouteType === 'bike';
            const mapMaxDistance = isBike ? this._model.mapMaxDistance * 2 : this._model.mapMaxDistance;
            if (totalDistance >= mapMaxDistance) {
                this.leaveFullscreen();

                // if (this.alertPopup) {
                // const hasOnlyPoint = this._mapModel.linePoints.length === 1;
                //
                //     this.alertPopup.fire({
                //         title: I18n.t('js.maps.errors.distance.title'),
                //         text: I18n.t('js.maps.errors.distance.text', {distance: parseInt(mapMaxDistance) / 1000}),
                //         confirmButtonText: I18n.t('js.maps.errors.distance.remove_last_point'),
                //         icon: 'warning',
                //         showDenyButton: hasOnlyPoint,
                //         denyButtonText: I18n.t('js.maps.errors.distance.remove_first_point'),
                //     })
                //         .then(function (event) {
                //             if (event.isConfirmed) {
                //                 // Remove last point
                //                 this.undo();
                //             } else if (event.isDenied) {
                //                 this.clear();
                //             }
                //         }.bind(this));
                // }

                this.undo();

                this.leaveFullscreen();

                if (this._model.showProductModal) {
                    this._model.showProductModal('map_max_distance');
                }

                return;
            }

            // Limit road computation usage
            if (this._model.hasMapLimitations && this._model.mapMaxRouteComputationCount) {
                let mapMaxRouteComputationCount = StorageService.getItem('map_max_route_computation_count') || 0;

                mapMaxRouteComputationCount += 1;

                if (mapMaxRouteComputationCount > this._model.mapMaxRouteComputationCount) {
                    if (this._model.snapToRoads) {
                        AnalyticsService.trackMapDistanceMaxReached();

                        if (this._model.showProductModal) {
                            this._model.showProductModal('map_max_route_computation_count');
                        }
                    }

                    if (this.hasModule('TOOLBAR')) {
                        this.disableSnapToRoad();
                    }

                    callback();

                    return;
                }

                StorageService.setItem('map_max_route_computation_count', mapMaxRouteComputationCount);
            }
        }

        this._model.isComputingRoad = true;

        // Computation time is very low, so it's useless
        // this.toggleLoader(I18n.t('js.maps.toolbar.snap_to_road.computing'));

        let pointsArray;
        if (wayPointLatLng !== null && typeof wayPointLatLng !== 'undefined') {
            pointsArray = [startPointLatLng, wayPointLatLng, endPointLatLng];
        } else {
            pointsArray = [startPointLatLng, endPointLatLng];
        }

        this._model.currentRoutingService ||= this.selectRoutingService(pointsArray[0]);

        this._fetchRoute(pointsArray, callback, {
            routeType: routeType || this._model.routingRouteType,
            routingService: this._model.currentRoutingService
        });

        // Cancel if no results
        setTimeout(() => {
            this._model.isComputingRoad = false;
            // this.toggleLoader();
        }, FetchTimeout);
    },

    /* Private Methods
    ******************** */
    _fetchRoute(pointsArray, callback, {
        routeType = undefined,
        routingService = undefined,
        elevation = false
    } = {}) {
        MapRequest.getDirectionsAPIUrl(pointsArray, routingService, {
            routeType,
            elevation
        }, {timeout: FetchTimeout})
            .then((data) => {
                this._model.routingService = routingService;

                if (data && (data.errors === 'Bad Request' || (typeof data.errors === 'string' && data.errors.startsWith('Cannot find point'))) && routingService !== MapInterface.routingService.MAPBOX) {
                    AnalyticsService.trackRoutingError(data.errors);
                    this._model.currentRoutingService = MapInterface.routingService.MAPBOX;
                    this._fetchRoute(pointsArray, callback, {
                        routeType: this._model.routingRouteType,
                        routingService: this._model.currentRoutingService
                    });
                    return;
                }

                if (!data || data.errors) {
                    if (data?.errors) {
                        AnalyticsService.trackRoutingError(data.errors);
                    }

                    Notification.error(I18n.t('js.maps.toolbar.snap_to_road.error'));

                    callback();

                    return;
                } else if (data.abort) {
                    Notification.error(I18n.t('js.maps.toolbar.snap_to_road.network_error'));

                    callback();

                    return;
                }

                let encodedPoints;
                const routeProperties = {};
                if (routingService === MapInterface.routingService.MAPBOX) {
                    encodedPoints = data.routes && data.routes.length > 0 ? data.routes[0].geometry : null;
                    if (data.routes?.[0]) {
                        routeProperties.distance = data.routes[0].distance;
                        routeProperties.duration = data.routes[0].duration * 1000;
                    }
                } else if (data.paths) {
                    encodedPoints = data.paths && data.paths.length > 0 ? data.paths[0].points : null;
                    if (data.paths[0]) {
                        routeProperties.elevationGain = data.paths[0].ascend;
                        routeProperties.elevationLoss = data.paths[0].descend;
                        routeProperties.distance = data.paths[0].distance;
                        routeProperties.duration = data.paths[0].time;
                        routeProperties.surfaceDetails = data.paths[0].details?.surface;
                        routeProperties.roadClassDetails = data.paths[0].details?.road_class;
                    }
                }

                if (!encodedPoints) {
                    Notification.error(I18n.t('js.maps.toolbar.snap_to_road.error'));
                    callback();
                    return;
                }

                const currentRoute = [];
                const decodedArray = Polyline.decode(encodedPoints, {elevation});
                // loop through steps, create latlng
                for (let j = 0; j < decodedArray.length; j++) {
                    currentRoute.push({
                        lat: decodedArray[j][0],
                        lng: decodedArray[j][1]
                    });
                }

                if (routeProperties.roadClassDetails) {
                    for (const [from, to, roadClass] of routeProperties.roadClassDetails) {
                        for (let k = from; k < to && k < currentRoute.length; k++) {
                            currentRoute[k].roadClass = roadClass;
                        }
                    }
                }

                if (routeProperties.surfaceDetails) {
                    for (const [from, to, surfaceType] of routeProperties.surfaceDetails) {
                        for (let k = from; k < to && k < currentRoute.length; k++) {
                            if (surfaceType && surfaceType !== 'missing') {
                                currentRoute[k].surface = surfaceType;
                            } else {
                                currentRoute[k].surface = MapInterface.surfaceFromRoadClass(currentRoute[k].roadClass);
                            }
                        }
                    }
                }

                callback(currentRoute, routeProperties);
            })
            .catch((error) => {
                pushError(error);

                Notification.error(I18n.t('js.maps.toolbar.snap_to_road.network_error'));

                callback();
            })
            .finally(() => {
                setTimeout(() => {
                    this._model.isComputingRoad = false;
                    // this.toggleLoader();
                }, 100);
            });
    }
};

export default ComputeRoadMapModule;
